import React, { useState, useRef, useCallback, useEffect, startTransition } from 'react';
import clsx from 'clsx';

export const CodeInputs = React.memo(({
                                        code: controlledCode,
                                        error,
                                        loading,
                                        resending,
                                        disableSendingSessionKey,
                                        onCodeChange,
                                        onInputFocus,
                                        onKeyDown: onKeyDownProp,
                                        onPaste: onPasteProp,
                                        onMouseEnter,
                                        onMouseLeave,
                                        onInputBlur,
                                        isHovering,
                                        isFocused,
                                        inputRefs: inputRefsProp,
                                        styles,
                                        onClearError,
                                        onFocusChange,
                                      }) => {
  const [internalCode, setInternalCode] = useState(['', '', '', '', '', '']);
  const internalRefs = useRef([]);
  
  const isControlled = controlledCode !== undefined;
  const code = isControlled ? controlledCode : internalCode;
  const inputRefs = inputRefsProp || internalRefs;

  const updateCode = useCallback((newCode, index) => {
    if (onCodeChange) {
      onCodeChange(index, newCode[index]);
    } else if (!isControlled) {
      setInternalCode(newCode);
    }
  }, [onCodeChange, isControlled]);

  const updateCodeArray = useCallback((newCode) => {
    if (onCodeChange) {
      startTransition(() => {
        newCode.forEach((digit, i) => {
          onCodeChange(i, digit);
        });
      });
    } else if (!isControlled) {
      setInternalCode(newCode);
    }
  }, [onCodeChange, isControlled]);

  const handleCodeChange = useCallback((index, value) => {
    if (!/^\d*$/.test(value)) return;

    if (onClearError) {
      onClearError();
    }

    const newCode = [...code];

    if (value.length > 1) {
      const digits = value.substring(0, 6).split('');
      for (let i = 0; i < 6; i++) {
        newCode[i] = digits[i] || '';
      }
      
      updateCodeArray(newCode);

      const lastFilledIndex = Math.min(digits.length, 6) - 1;
      const nextIndex = Math.min(lastFilledIndex + 1, 5);
      setTimeout(() => {
        const nextInput = inputRefs.current[nextIndex];
        if (nextInput && nextInput.focus) {
          nextInput.focus();
          if (newCode[nextIndex] && nextInput.setSelectionRange) {
            nextInput.setSelectionRange(1, 1);
          } else if (nextInput.setSelectionRange) {
            nextInput.setSelectionRange(0, 0);
          }
        }
      }, 0);
      return;
    }

    newCode[index] = value;
    updateCode(newCode, index);

    if (value && index < 5) {
      setTimeout(() => {
        inputRefs.current[index + 1]?.focus();
        if (newCode[index + 1]) {
          inputRefs.current[index + 1]?.setSelectionRange(1, 1);
        } else {
          inputRefs.current[index + 1]?.setSelectionRange(0, 0);
        }
      }, 0);
    } else if (value && index === 5) {
      setTimeout(() => {
        inputRefs.current[index]?.blur();
      }, 0);
    }
  }, [code, isControlled, onCodeChange, onClearError, inputRefs, updateCode]);

  const handleInputFocus = useCallback((index) => {
    if (onFocusChange) {
      onFocusChange(true);
    }
    
    if (onInputFocus) {
      onInputFocus(index);
    }

    setTimeout(() => {
      if (code[index]) {
        inputRefs.current[index]?.setSelectionRange(1, 1);
      } else {
        inputRefs.current[index]?.setSelectionRange(0, 0);
      }
    }, 0);
  }, [code, onInputFocus, onFocusChange, inputRefs]);

  const handleKeyDown = useCallback((index, e) => {
    if (onKeyDownProp) {
      onKeyDownProp(index, e);
      return;
    }

    if (e.key === 'Backspace') {
      e.preventDefault();
      
      if (onClearError) {
        onClearError();
      }

      const newCode = [...code];

      if (newCode[index]) {
        newCode[index] = '';
        updateCode(newCode, index);
      } else if (index > 0) {
        newCode[index - 1] = '';
        updateCode(newCode, index - 1);
        setTimeout(() => {
          inputRefs.current[index - 1]?.focus();
          if (newCode[index - 1]) {
            inputRefs.current[index - 1]?.setSelectionRange(1, 1);
          } else {
            inputRefs.current[index - 1]?.setSelectionRange(0, 0);
          }
        }, 0);
      }
    }

    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      handleCodeChange(index, e.key);
    }

    if (e.key === 'Delete') {
      e.preventDefault();
      
      if (onClearError) {
        onClearError();
      }

      const newCode = [...code];
      newCode[index] = '';
      updateCode(newCode, index);
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      setTimeout(() => {
        document.getElementById(`code-input-${index - 1}`)?.focus();
      }, 0);
      e.preventDefault();
    }

    if (e.key === 'ArrowRight' && index < 5) {
      setTimeout(() => {
        document.getElementById(`code-input-${index + 1}`)?.focus();
      }, 0);
      e.preventDefault();
    }
  }, [code, onKeyDownProp, onClearError, handleCodeChange, updateCode, inputRefs]);

  const handlePaste = useCallback((e) => {
    if (onPasteProp) {
      onPasteProp(e);
      return;
    }

    e.preventDefault();
    
    if (onClearError) {
      onClearError();
    }

    const clipboardText = e.clipboardData.getData('text/plain');
    if (!clipboardText) {
      return;
    }

    const sanitized = clipboardText.replace(/\s/g, '');
    if (!/^\d{6}$/.test(sanitized)) {
      return;
    }

    const digitsArray = sanitized.split('');
    const newCode = Array(6).fill('').map((_, i) => digitsArray[i] || '');

    updateCodeArray(newCode);

    const lastFilledIndex = Math.min(digitsArray.length - 1, 5);
    const nextIndex = Math.min(lastFilledIndex + 1, 5);
    setTimeout(() => {
      inputRefs.current[nextIndex]?.focus();
      if (newCode[nextIndex]) {
        inputRefs.current[nextIndex]?.setSelectionRange(1, 1);
      } else {
        inputRefs.current[nextIndex]?.setSelectionRange(0, 0);
      }
    }, 0);
  }, [code, onPasteProp, onClearError, inputRefs, updateCodeArray]);

  useEffect(() => {
    if (isControlled && controlledCode) {
      setInternalCode(controlledCode);
    }
  }, [isControlled, controlledCode]);

  return (
    <div
      className={styles.codeInputContainer}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {code.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          id={`code-input-${index}`}
          type="text"
          inputMode="numeric"
          className={clsx(styles.codeInput, {
            [styles.error]: error && !resending && !isHovering && !isFocused
          })}
          value={digit}
          onChange={(e) => handleCodeChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={() => handleInputFocus(index)}
          onBlur={onInputBlur}
          onPaste={handlePaste}
          maxLength={1}
          disabled={loading || resending || disableSendingSessionKey}
        />
      ))}
    </div>
  );
});
